import cn from 'classnames'
import { formatCurrency, formatNumber, noAmountPlug } from 'utils/formatters'
import { getHashExplorerLink } from 'utils/get-hash-explorer-link'

import { useCurrency } from 'utils/use-currency'
import { ICurrency } from 'store/fetchers/currencies'

import { useEffect, useMemo, useState } from 'react'
import { parse } from 'date-fns'

import Notice from 'components/ui/notice'
import { CopyButtonWithSuccess } from 'components/ui/copy-button-with-success'

import { floorNumber } from '../../../../../utils/floor-number'

import Tooltip from '../../../../global/tooltip'

import { TransactionAccountType } from '../../../../../store/fetchers/users/types'

import { CountdownTimer, STATUS } from '../../../countdown-timer'

import { formatDateForMobile, formatHash, getStatusColor, getImage, getTypeText, formatDate } from './utils'

import { TransactionTypeView, TransactionStatusView } from './enum'

import styles from './styles.module.scss'
import { useTranslation } from 'utils/use-translation'

export interface TransactionMain {
  index?: number
  id?: string
  code: string
  network: string
  incoming?: boolean
  date: string
  type: TransactionTypeView
  amount: string
  amountTo?: string
  amountFrom?: string
  to_currency?: { code: string; network: string }
  from_currency?: { code: string; network: string }
  received_amount?: number
  hash: string
  status?: TransactionStatusView
  accountType?: TransactionAccountType
}

export function Transaction({
  index,
  id,
  code,
  network,
  date,
  type,
  amount,
  amountTo,
  amountFrom,
  to_currency,
  from_currency,
  hash,
  status,
}: TransactionMain) {
  const { t } = useTranslation()
  
  const [maxAmountLength, setMaxAmountLength] = useState<number>(14)
  const currency: ICurrency = useCurrency({
    code: code,
    network: network,
  })

  const currencyTo: ICurrency = useCurrency({
    code: to_currency?.code,
    network: to_currency?.network,
  })

  const currencyFrom: ICurrency = useCurrency({
    code: from_currency?.code,
    network: from_currency?.network,
  })

  const transactionColorClass = useMemo(() => {
    return index % 2 === 0 ? styles.evenTransaction : styles.oddTransaction
  }, [index])

  const typeImage = getImage(type)

  const formatDateLocal = () => {
    // Преобразуем строку даты в более совместимый формат
    const formattedDateString = date.replace(' ', 'T').replace(' +00:00', 'Z')

    // Преобразуем строку даты в объект Date
    const newDate = parse(formattedDateString, "yyyy-MM-dd'T'HH:mm:ss.SSSX", new Date())

    return new Date(newDate.toUTCString())
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth > 765) {
        setMaxAmountLength(14)
      } else {
        setMaxAmountLength(10)
      }
    }
  }, [])

  const isExchangeTx = amountTo && amountFrom && from_currency && to_currency
  return (
    <div
      className={cn(styles.transaction, transactionColorClass, {
        [styles.paddingExchange]: isExchangeTx,
      })}
    >
      <div className={styles.date}>{formatDate(date)}</div>

      <div className={styles.type}>
        {typeImage && <img src={typeImage} alt={type} />}
        <span>{t(getTypeText(type).toLowerCase())}</span>
      </div>

      <div className={styles.amount}>
        {amountFrom !== amountTo ||
        currencyFrom?.code !== currencyTo?.code ||
        currencyFrom?.network !== currencyTo?.network ? (
          <>
            <div className={styles.value}>
              <span className={styles.valueAmount}>
                {!isNaN(Number(amountTo))
                  ? `${
                      floorNumber(+amountTo, 6).toString().length >= maxAmountLength
                        ? formatNumber(floorNumber(+amountTo, 6))
                            .toString()
                            .slice(0, maxAmountLength - 2) + '...'
                        : formatNumber(floorNumber(+amountTo, 6))
                    }`
                  : noAmountPlug}
              </span>
              {floorNumber(+amountTo, 6).toString().length >= maxAmountLength ? (
                <Tooltip className={styles.tooltip}>
                  <p>{formatNumber(floorNumber(+amountTo, 6))}</p>
                </Tooltip>
              ) : (
                <></>
              )}
              <span>{formatCurrency(currencyTo)}</span>
            </div>

            <div className={styles.valueBottom}>
              <span className={styles.valueAmount}>
                {!isNaN(Number(amountFrom))
                  ? `${
                      floorNumber(+amountFrom, 6).toString().length >= maxAmountLength
                        ? formatNumber(floorNumber(+amountFrom, 6))
                            .toString()
                            .slice(0, maxAmountLength - 2) + '...'
                        : formatNumber(floorNumber(+amountFrom, 6))
                    }`
                  : noAmountPlug}
              </span>
              {floorNumber(+amountFrom, 6).toString().length >= maxAmountLength ? (
                <Tooltip className={styles.tooltip}>
                  <p>{formatNumber(floorNumber(+amountFrom, 6))}</p>
                </Tooltip>
              ) : (
                <></>
              )}
              <span>{formatCurrency(currencyFrom)}</span>
            </div>
          </>
        ) : (
          <div className={styles.value}>
            <span className={styles.valueAmount}>
              {!isNaN(Number(amount))
                ? `${
                    floorNumber(+amount, 6).toString().length >= maxAmountLength
                      ? formatNumber(floorNumber(+amount, 6))
                          .toString()
                          .slice(0, maxAmountLength - 2) + '...'
                      : formatNumber(floorNumber(+amount, 6))
                  }`
                : noAmountPlug}
            </span>
            {floorNumber(+amount, 6).toString().length >= maxAmountLength ? (
              <Tooltip className={styles.tooltip}>
                <p>{formatNumber(floorNumber(+amount, 6))}</p>
              </Tooltip>
            ) : (
              <></>
            )}
            <span>{formatCurrency(currency)}</span>
          </div>
        )}
      </div>

      <div className={styles.hashOrId}>
        {hash && hash !== '-' ? (
          <div className={styles.hash}>
            <a href={getHashExplorerLink(currency, hash)} target="_blank" rel="noreferrer">
              <span>{formatHash(hash)}</span>
            </a>
            <CopyButtonWithSuccess value={hash} className={styles.copy} grey />
          </div>
        ) : id ? (
          <div className={styles.hash}>
            <span>{id}</span>
            <CopyButtonWithSuccess value={id} className={styles.copy} grey />
          </div>
        ) : (
          <span>-</span>
        )}
      </div>

      <div className={styles.status}>
        <div className={styles.date}>{formatDateForMobile(date)}</div>
        <div className={styles[getStatusColor(status)]}>
          {status === TransactionStatusView.REJECTED ? t('rejected') : t(status.toLowerCase())}
        </div>
        {status === TransactionStatusView.PENDING && type !== TransactionTypeView.WITHDRAWAL && (
          <CountdownTimer type={STATUS} creationDate={formatDateLocal()} />
        )}
        {status === TransactionStatusView.REJECTED ? (
          <Notice boxPosition={styles.noticeManualCheck} alt={`Internal transaction`}>{t('transaction_rejected')}</Notice>
        ) : (
          ''
        )}
      </div>
    </div>
  )
}
